import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiExternalLink } from 'react-icons/fi';
import { BUY_LABEL, getBuyableBooks } from '../data/library';

const BUYABLE_BOOKS = getBuyableBooks();
const INTERVAL_MS = 9000;

export default function AmazonBooksCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = BUYABLE_BOOKS.length;

  const go = useCallback(
    dir => {
      setIndex(prev => (prev + dir + count) % count);
    },
    [count]
  );

  const goTo = useCallback(
    i => {
      setIndex(((i % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (paused || count < 2) return undefined;
    const id = window.setInterval(() => go(1), INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [paused, go, count]);

  return (
    <div
      className="amazon-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Buy now"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <div className="amazon-carousel__stage">
        {BUYABLE_BOOKS.map((book, i) => (
          <article
            key={book.id}
            id={`amazon-book-${book.id}`}
            className={`amazon-carousel__slide${i === index ? ' is-active' : ''}`}
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
          >
            <div className="amazon-layout">
              <a
                href={book.buyUrl}
                className="amazon-thumb-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Buy ${book.title} (opens in new tab)`}
              >
                <img
                  src={book.cover}
                  alt={book.coverAlt}
                  className="amazon-thumb"
                  width={176}
                  height={264}
                  decoding="async"
                />
              </a>
              <div className="amazon-copy">
                <p className="section-eyebrow">{book.amazonEyebrow || 'Buy now'}</p>
                <h2 className="section-display section-display--sm">{book.title}</h2>
                <p className="amazon-tagline">{book.tagline}</p>
                <a
                  href={book.buyUrl}
                  className="btn-buy-amazon"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {BUY_LABEL}
                  <FiExternalLink size={20} aria-hidden />
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>

      {count > 1 ? (
        <div className="amazon-carousel__controls">
          <button type="button" className="amazon-carousel__arrow" onClick={() => go(-1)} aria-label="Previous book">
            <FiChevronLeft size={20} aria-hidden />
          </button>
          <div className="amazon-carousel__dots" role="tablist" aria-label="Choose book">
            {BUYABLE_BOOKS.map((book, i) => (
              <button
                key={book.id}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-controls={`amazon-book-${book.id}`}
                className={`amazon-carousel__dot${i === index ? ' is-active' : ''}`}
                onClick={() => goTo(i)}
              >
                <span className="visually-hidden">{book.title}</span>
              </button>
            ))}
          </div>
          <button type="button" className="amazon-carousel__arrow" onClick={() => go(1)} aria-label="Next book">
            <FiChevronRight size={20} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
}
